<template>
  <v-container fluid>
    <el-overlay-loading :loading="pageloading" />
    <h3 class="mb-5">Asmenys</h3>

    <v-row v-if="showWarning" align="baseline" class="mb-0">
      <v-col cols="12">
        <v-alert dense outlined type="error" color="orange">
          {{ warningMsg }}
        </v-alert>
      </v-col>
    </v-row>

    <v-data-table
      :headers="filteredHeaders"
      :items="itemsList"
      :loading="loading"
      :search="search"
      :item-class="rowClass"
      :footer-props="{
        showFirstLastPage: true,
        showCurrentPage: true,
        itemsPerPageText: 'Rodyti po:',
        itemsPerPageAllText: 'visi',
        itemsPerPageOptions: itemsPerPageOptions,
      }"
      :hide-default-footer="hideFooter"
      :items-per-page="pageSize"
    >
      <template #top>
        <v-toolbar flat>
          <v-text-field
            v-model="search"
            prepend-icon="mdi-magnify"
            label=""
            single-line
            hide-details
          ></v-text-field>
          <template v-if="!isReadonlyForm && !isPrasymasPirmenybe">
            <v-spacer></v-spacer>
            <el-button-prideti
              small
              primary
              @click.native.stop="newItem()"
              :disabled="disablePridetiAsmeni"
            >
              Pridėti asmenį
            </el-button-prideti>
          </template>
        </v-toolbar>
      </template>

      <template #[`item.asm_pavadinimas`]="{ item }">
        {{ item.asm_pavadinimas }}
        <span v-if="item.yra_mires" class="text-gray">(asmuo miręs)</span>

        <div v-if="item.isAtstovas" class="text-gray">
          <v-divider />
          <v-row dense no-gutters>
            <v-col cols="12" md="12">
              <span class="text-gray">Atstovas:</span>
            </v-col>
            <v-col cols="12" md="12">
              <span class="text-gray">{{ item.atstovas_pavadinimas }}</span>
            </v-col>
            <v-col cols="12" md="12">
              <span
                class="text-gray"
                v-html="item.atstovas_veikiantis_nowrap"
              ></span>
            </v-col>
            <v-col cols="12" md="12">
              <span
                class="text-warning"
                v-html="item.atstovas_ispejimas"
              ></span>
            </v-col>
          </v-row>
        </div>
      </template>
      <template #[`item.pareiskejas`]="{ item }">
        {{ item.sasm_pareiskejas ? 'Taip' : '' }}
      </template>
      <template #[`item.sasm_el_pastai`]="{ item }">
        {{ item.sasm_el_pastai.join(', ') }}
      </template>
      <template #[`item.actions`]="{ item }">
        <el-table-action
          :item="item"
          @editItem="editItem(item)"
          @viewItem="editItem(item)"
          @deleteItem="deleteItem(item)"
          @addAtstovas="addAtstovas(item.atstovas)"
          @editAtstovas="editAtstovas(item.atstovas)"
          @viewAtstovas="editAtstovas(item.atstovas)"
          @deleteAtstovas="deleteAtstovas(item.atstovas)"
        />
      </template>
      <template slot="loading">
        Kraunasi...
      </template>
      <template slot="no-results">
        Duomenų nėra
      </template>
      <template slot="no-data">
        Duomenų nėra
      </template>
      <template #[`footer.page-text`]="props">
        Rodoma {{ props.pageStart }}-{{ props.pageStop }} iš
        {{ props.itemsLength }}
      </template>
    </v-data-table>

    <modal-prasymas-asmuo
      :key="modalAsmuoKey"
      v-model="showAsmuo"
      :asmuo="modalAsmuo"
      :asmuo-role="modalAsmuoRole"
      :change-role="modalChangeRole"
      :parent-atstovas-id="modalParentAtstovasId"
      :salys="salys"
      :force-foreign="forceForeign"
      :readonly="modalAsmuoReadonly"
      :save-fail="modalAsmuoSaveFail"
      @savedData="saveAsmuo"
      @savedAtstovas="savedAtstovas"
    />

    <modal-delete
      title="Šalinti asmenį?"
      :text="modalDeleteText"
      v-model="showDelete"
      :item="modalDeleteItem"
      @confirmDelete="confirmDelete"
    />

    <v-footer class="form-footer" v-if="!isReadonly">
      <v-spacer></v-spacer>
      <el-button
        primary
        @click.native="checkErrorsClick()"
        :loading="loadingTikrinti"
        >Tikrinti</el-button
      >
    </v-footer>
  </v-container>
</template>

<script>
import { mapGetters } from 'vuex'
import checkErrors from '@/mixins/checkErrors'
import ModalPrasymasAsmuo from '../modals/ModalPrasymasAsmuo'
import ModalDelete from '../modals/ModalDelete'
import NtrPaslaugosMap from '../ntrpaslaugos/NtrPaslaugosMap'
import rowClass from '@/mixins/rowClass'
import busena from '@/mixins/busenaNtr'
import userRole from '@/mixins/userRole'

export default {
  name: 'RegPrasymasAsmenys',
  components: {
    ModalPrasymasAsmuo,
    ModalDelete,
  },
  props: {
    readonlyForm: { type: Boolean, default: false },
    readonly: { type: Boolean, default: false },
  },
  mixins: [busena, rowClass, userRole, checkErrors],
  data() {
    return {
      pageloading: true,
      loading: true,
      search: null,
      showAsmuo: false,
      modalAsmuoReadonly: false,
      modalAsmuoKey: 'asmuo',
      modalAsmuoRefresh: 0,
      modalAsmuoRole: null,
      modalChangeRole: false,
      modalParentAtstovasId: null,
      modalAsmuo: {},
      showDelete: false,
      modalDeleteVardas: '',
      modalDeleteItem: null,
      modalDeleteRole: null,
      asmenys: [],
      salys: [],
      itemsPerPageOptions: [10, 50, -1],
      pageSize: 10,
      showFail: false,
      failMsg: null,
      showWarning: false,
      warningMsg: null,
      modalAsmuoSaveFail: false,
    }
  },
  computed: {
    ...mapGetters(['prasymasID', 'prasymasSistema']),
    isSistemaSavitarna() {
      return this.prasymasSistema === 'savitarna'
    },
    headers() {
      return [
        {
          text: 'Sutarties šalis',
          align: 'start',
          value: 'asmuoRole',
          hide:
            this.isPrasymasSeima ||
            this.isPrasymasBendra ||
            this.isPrasymasPirmenybe,
        },
        {
          text: 'Pareiškėjas',
          align: 'center',
          value: 'pareiskejas',
          hide: !this.isPrasymasSeima,
          width: 100,
        },
        {
          text: 'Asmens kodas',
          align: 'start',
          value: 'asm_asmens_kodas',
          width: 150,
        },
        {
          text: this.titleVardasPavarde,
          align: 'start',
          value: 'asm_pavadinimas',
        },
        {
          text: 'Adresas',
          align: 'start',
          value: 'adr_text',
        },
        {
          text: 'Patvirtinimas',
          align: 'center',
          value: 'patvirtinimas',
          width: 100,
          hide: this.hidePatvirtinimas,
        },
        {
          text: 'Veiksmai',
          align: 'center',
          sortable: false,
          value: 'actions',
        },
      ]
    },
    filteredHeaders() {
      return this.headers.filter(item => {
        return !item.hide
      })
    },
    titleVardasPavarde() {
      if (this.isPrasymasNuoma || this.isPrasymasPanauda) {
        return 'Vardas Pavardė / Pavadinimas'
      }
      if (this.isPrasymasPirmenybe && this.yraAsmuoJA) {
        return 'Pavadinimas'
      }
      return 'Vardas Pavardė'
    },
    itemsList() {
      return this.asmenys.map((item, index) => {
        item.asmuoRole = NtrPaslaugosMap.getAsmuoRole(
          item.sasm_role_objekte
        ).title

        if (!item.asm_asmens_kodas) {
          item.asm_asmens_kodas = item.asm_gimimo_data
        }

        item.canEdit = !this.isReadonlyForm
        item.canView = this.isReadonlyForm
        item.canDelete = !this.isReadonlyForm && item.galima_salinti
        item.readonly = this.isReadonlyForm

        if (
          this.isRengiamas ||
          item.yra_mires ||
          (this.isSistemaSavitarna &&
            this.isNutrukusSantuokai &&
            !item.sasm_pareiskejas)
        ) {
          item.patvirtinimas = null
        }

        item.atstovas = {}
        if (item.tipas === 'JUR' || this.isPrasymasPirmenybe) {
          item.atstovas = {
            parent_atstovas_id: item.sasm_id,
            sasm_id: item.atstovas_sasm_id,
            asm_pavadinimas: item.atstovas_pavadinimas,
            sasm_role_objekte: 'atstovas',
          }
          if (item.atstovas_sasm_id) {
            item.canViewAtstovas = true
            item.canEditAtstovas = false
            item.canDeleteAtstovas =
              (item.canDelete ||
                (!this.isReadonlyForm && this.isPrasymasPirmenybe)) &&
              item.atstovas_galima_salinti &&
              this.showAtstovas
          } else {
            item.canAddAtstovas =
              item.canEdit && item.atstovas_galima_salinti && this.showAtstovas
          }
        }

        item.isAtstovas = item.atstovas_sasm_id
        item.isLast = index === this.asmenys.length - 1

        // item.atstovas_veikiantis =
        //   'Veikiantis pagal įgaliojimą Nr. 18939, registravimo data 2022-08-19, terminas 2024-08-18'

        item.atstovas_veikiantis_nowrap = ''
        if (item.atstovas_veikiantis) {
          item.atstovas_veikiantis_nowrap = `<span class="nowrap">${item.atstovas_veikiantis.replaceAll(
            ',',
            ',</span> <span class="nowrap">'
          )}</span>`
        }
        if (this.isPrasymasPirmenybe) {
          item.atstovas_veikiantis_nowrap = item.atstovas_veikiantis
        }
        if (item.atstovas_ispejimas) {
          item.atstovas_ispejimas = item.atstovas_ispejimas.replace(
            '.',
            '. <br/>'
          )
        }
        return item
      })
    },
    yraAsmuoJA() {
      return (
        this.itemsList.filter(item => {
          return item.tipas === 'JUR'
        }).length > 0
      )
    },
    hideFooter() {
      return this.itemsList.length <= 10
    },
    isReadonly() {
      return this.readonly
    },
    isReadonlyForm() {
      return this.readonlyForm
    },
    hidePatvirtinimas() {
      return (
        !this.isSistemaSavitarna ||
        (this.isPrasymasNuoma && this.isPrasymasIsregistravimas) ||
        (this.isPrasymasPanauda && this.isPrasymasIsregistravimas) ||
        (this.isPrasymasSubnuoma && this.isPrasymasIsregistravimas) ||
        (this.isPrasymasPreliminari && this.isPrasymasIsregistravimas)
      )
    },
    isLocal() {
      return process.env.VUE_APP_LOCAL === 'local'
    },
    asmuoRole() {
      if (this.isPrasymasPanauda) {
        return 'panauda_gavejas'
      }
      if (this.isPrasymasNuoma) {
        return 'nuomininkas'
      }
      return 'savininkas'
    },
    showAtstovas() {
      return (
        this.isPrasymasNuoma ||
        this.isPrasymasPanauda ||
        this.isPrasymasPirmenybe
      )
    },
    modalDeleteText() {
      return `Ar norite pašalinti asmenį <strong>${
        this.modalDeleteVardas
      }</strong>?${
        (this.isPrasymasSeima &&
          (this.isPrasymasIregistravimas || this.isPrasymasPerkelimas)) ||
        this.isPrasymasBendra
          ? '<br/>Kartu bus pašalintas ir sutuoktinis.'
          : ''
      }`
    },
    disablePridetiAsmeni() {
      return (
        (this.isPrasymasSeima || this.isPrasymasBendra) &&
        this.asmenys.length > 1
      )
    },
    forceForeign() {
      return (
        ((this.isPrasymasSeima &&
          (this.isPrasymasIregistravimas || this.isPrasymasPerkelimas)) ||
          this.isPrasymasBendra) &&
        this.asmenys.length > 0 &&
        !this.asmenys[0].foreign
      )
    },
  },
  created() {
    this.getData()
  },
  methods: {
    getData() {
      this.pageloading = true
      this.loading = true
      this.loadingTikrinti = true
      this.showWarning = false
      this.warningMsg = null

      this.$axios
        .get(`ntrpaslaugos/prasymas/${this.prasymasID}/asmuo`)
        .then(res => {
          this.asmenys = res.data.asmenys
          this.salys = res.data.salys
          this.warningMsg = res.data.warning
          if (this.warningMsg) {
            this.showWarning = true
          }
          this.updateErrors()
        })
        .catch(err => {
          window._VMA.$emit('DATA_ERROR', err.response.data.message)
        })
        .finally(() => {
          this.loading = false
          this.loadingTikrinti = false
          this.pageloading = false
        })
    },
    editItem(item) {
      this.openAsmuo(item)
    },
    newItem() {
      if (
        this.isPrasymasSeima &&
        this.isPrasymasIsregistravimas &&
        this.asmenys.length == 2
      ) {
        this.failMsg =
          'Jau įvesti buvusio sutuoktinio duomenys. Dviejų asmenų pridėjimas negalimas.'
        this.showFail = true
        return
      }
      this.openAsmuo({})
    },
    openAsmuo(item) {
      this.showFail = false
      this.modalAsmuo = {
        asm_asmens_kodas_jur: null,
        asm_pavadinimas: null,
        asm_asmens_kodas_fiz: null,
        asm_asmens_kodas: this.isLocal ? '39000000000' : null,
        asm_fiz_vardas: this.isLocal ? 'PETRAS' : null,
        asm_fiz_pavarde: this.isLocal ? 'PETRAUSKAS' : null,
        asm_asmens_kodas_fiz: null,
        asm_asmens_kodas_jur: null,
        asm_gimimo_data: null,
        asm_vls_kodas: null,
        foreign: this.forceForeign,
        ...item,
        adresas: {
          text: item.adr_text,
        },
      }
      this.modalAsmuoReadonly = item.readonly
      this.modalAsmuoRole = item.sasm_role_objekte ?? this.asmuoRole
      this.modalChangeRole = !item.sasm_id //|| item.galima_salinti
      this.modalParentAtstovasId = null
      this.modalAsmuoSaveFail = false
      this.modalAsmuoKey =
        'asmuo' + (item.sasm_id ?? 'new' + this.modalAsmuoRefresh++)
      this.showAsmuo = true
    },
    saveAsmuo(asmuo, atstovas = false) {
      const data = {
        sasm_id: asmuo.sasm_id,
        pra_id: this.prasymasID,
        amn_id: asmuo.amn_id,
        sasm_role_objekte:
          this.isNuoma || this.isPanauda
            ? asmuo.sasm_role_objekte
            : atstovas
            ? 'atstovas'
            : this.asmuoRole,
        tipas: asmuo.tipas,
        sasm_el_pastas: asmuo.sasm_el_pastas,
        adresas: {
          adresas_foreign: false,
          text: asmuo.adresas.text,
        },
        asm_asmens_kodas: asmuo.asm_asmens_kodas,
        asm_kodo_tipas: asmuo.asm_kodo_tipas,
        sasm_pareiskejas: asmuo.sasm_pareiskejas,
        sasm_atstovaujamas_id: atstovas ? asmuo.parent_atstovas_id : null,
        sasm_veikiantis_pagal: atstovas ? asmuo.sasm_veikiantis_pagal : null,
      }
      this.modalAsmuoSaveFail = false

      this.$axios
        .post(`ntrpaslaugos/prasymas/${this.prasymasID}/asmuo`, data)
        .then(res => {
          this.modalAsmuoKey = 'asmuo'
          // window._VMA.$emit('DATA_OK', res.data.message)
          this.getData()
          this.showAsmuo = false
        })
        .catch(err => {
          window._VMA.$emit('DATA_ERROR', err.response.data.message)
          this.modalAsmuoSaveFail = true
        })
        .finally(() => {
          this.loading = false
          this.showFail = false
        })
    },
    deleteItem(item) {
      this.modalDeleteVardas = item.asm_pavadinimas
      this.modalDeleteItem = Object.assign({}, item)
      this.modalDeleteRole = this.asmuoRole
      this.showDelete = true
    },
    confirmDelete(item) {
      this.confirmDeleteAsmuo(item)
    },
    confirmDeleteAsmuo(item) {
      this.loading = true

      this.$axios
        .delete(
          `ntrpaslaugos/prasymas/${this.prasymasID}/asmuo/${item.sasm_id}/${this.modalDeleteRole}`
        )
        .then(res => {
          window._VMA.$emit('DATA_OK', res.data.message)
          this.getData()
        })
        .catch(err => {
          window._VMA.$emit('DATA_ERROR', err.response.data.message)
        })
        .finally(() => {
          this.loading = false
        })
    },
    addAtstovas(item) {
      this.openAtstovas(null, item.parent_atstovas_id)
    },
    editAtstovas(item) {
      this.openAtstovas(item.sasm_id, item.parent_atstovas_id, true)
    },
    openAtstovas(sasm_id, parent_atstovas_id, readonly = false) {
      this.modalAsmuo = {
        sasm_id: sasm_id,
        asm_asmens_kodas: this.isLocal ? '39000000000' : null,
        asm_fiz_vardas: this.isLocal ? 'PETRAS' : null,
        asm_fiz_pavarde: this.isLocal ? 'PETRAUSKAS' : null,
        asm_asmens_kodas_fiz: null,
        asm_asmens_kodas_jur: null,
      }
      this.modalAsmuoReadonly = readonly
      this.modalAsmuoRole = 'atstovas'
      this.modalParentAtstovasId = parent_atstovas_id
      this.modalAsmuoKey =
        'asmuo' + (sasm_id ?? 'new' + this.modalAsmuoRefresh++)
      this.showAsmuo = true
    },
    savedAtstovas(modalAsmuo) {
      this.loading = true

      modalAsmuo.sasm_atstovaujamas_id = modalAsmuo.parent_atstovas_id
      this.saveAsmuo(modalAsmuo, true)

      // const data = {
      //   sasm_id: modalAsmuo.parent_atstovas_id,
      //   amn_id: modalAsmuo.amn_id,
      // }

      // this.$axios
      //   .post(
      //     `ntrpaslaugos/prasymas/${this.prasymasID}/asmuo/${modalAsmuo.parent_atstovas_id}/atstovas`,
      //     data
      //   )
      //   .then(res => {
      //     this.modalAsmuoKey = 'asmuo'
      //     // window._VMA.$emit('DATA_OK', res.data.message)
      //     this.showAsmuo = false
      //     this.getData()
      //   })
      //   .catch(err => {
      //     window._VMA.$emit('DATA_ERROR', err.response.data.message)
      //   })
      //   .finally(() => {
      //     this.loading = false
      //   })
    },
    deleteAtstovas(item) {
      this.modalDeleteVardas = item.asm_pavadinimas
      this.modalDeleteItem = Object.assign({}, item)
      this.modalDeleteRole = 'atstovas'
      this.showDelete = true
    },
    checkErrorsClick() {
      this.loadingTikrinti = true
      this.checkNtrErrors()
    },
  },
}
</script>
<style lang="scss" scoped>
.duomenys {
  padding-top: 10px;
  padding-bottom: 10px;

  p {
    padding: 3px 0px;
    margin: 0;
  }

  dl {
    display: flex;
    flex-flow: row wrap;
  }
  dt {
    flex-basis: 40%;
    padding: 2px 4px;
    text-align: right;
    font-weight: 400;
  }
  dd {
    flex-basis: 40%;
    flex-grow: 1;
    margin: 0;
    padding: 2px 4px;
    font-weight: 600;
  }
}
</style>
